import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ArrowUpRight, Phone, Mail } from "lucide-react";
import Logo from "../Logo";

const LINKS = [
  { label: "Home", href: "#top-v2" },
  { label: "Projects", href: "#projects-v2" },
  { label: "Philosophy", href: "#philosophy-v2" },
  { label: "Live Progress", href: "#progress-v2" },
  { label: "Contact", href: "#contact-v2" },
];

export default function NavV2() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        data-testid="nav-v2"
        className={`fixed top-0 inset-x-0 z-40 transition-[background-color,backdrop-filter,border-color] duration-500 ${
          scrolled
            ? "bg-charcoal/85 backdrop-blur-xl border-b border-charcoal_ink/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 h-[110px] flex items-center justify-between">
          {/* LEFT — Menu */}
          <div className="flex items-center gap-4 flex-1">
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="nav-v2-menu"
              aria-label="Open menu"
              className="group flex items-center gap-3 text-charcoal_ink hover:text-glow_lime transition-colors duration-500"
            >
              <span className="relative flex flex-col gap-1.5 w-6">
                <span className="h-px w-full bg-current" />
                <span className="h-px w-4 bg-current group-hover:w-full transition-all duration-300" />
              </span>
              <span className="hidden md:inline chapter-num tracking-[0.28em]">MENU</span>
            </button>
          </div>

          {/* CENTER — Logo (always light in dark v2) */}
          <Link to="/v2" data-testid="nav-v2-logo" className="absolute left-1/2 -translate-x-1/2 z-10">
            <Logo variant="light" size={140} />
          </Link>

          {/* RIGHT — Demo toggle + Search + Enquire */}
          <div className="flex items-center gap-3 flex-1 justify-end">
            {/* Demo toggle */}
            <div className="hidden lg:flex items-center gap-2 mr-2" data-testid="demo-toggle-v2">
              <Link
                to="/"
                className="px-3 py-1.5 chapter-num tracking-[0.24em] border rounded-full text-charcoal_ink/70 border-charcoal_ink/25 hover:text-glow_lime hover:border-glow_lime transition-colors"
              >
                DEMO 01
              </Link>
              <span className="chapter-num text-charcoal_ink/40">/</span>
              <span
                className="px-3 py-1.5 chapter-num tracking-[0.24em] rounded-full text-charcoal bg-glow_lime border border-glow_lime glow-box"
              >
                DEMO 02
              </span>
              <span className="chapter-num text-charcoal_ink/40">/</span>
              <Link
                to="/v3"
                className="px-3 py-1.5 chapter-num tracking-[0.24em] border rounded-full text-charcoal_ink/70 border-charcoal_ink/25 hover:text-glow_lime hover:border-glow_lime transition-colors"
              >
                DEMO 03
              </Link>
            </div>

            <button
              onClick={() => setSearchOpen(true)}
              data-testid="nav-v2-search"
              className="group flex items-center gap-2 text-charcoal_ink hover:text-glow_lime transition-colors duration-500"
              aria-label="Search"
            >
              <span className="hidden md:inline chapter-num tracking-[0.28em]">SEARCH</span>
              <Search className="w-4 h-4" />
            </button>
            <a
              href="#contact-v2"
              data-testid="nav-v2-cta"
              className="hidden md:inline-flex items-center gap-2 h-10 px-5 text-[11px] font-semibold tracking-[0.22em] uppercase transition-all duration-500 bg-glow_lime text-charcoal hover:bg-charcoal_ink hover:text-charcoal glow-box"
            >
              Enquire <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </motion.header>

      {/* LEFT DRAWER MENU */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="fixed inset-0 z-50 bg-charcoal/70 backdrop-blur-sm"
              onClick={() => setMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              data-testid="nav-v2-drawer"
              className="fixed inset-y-0 left-0 z-50 w-full sm:w-[440px] md:w-[520px] bg-charcoal_2 text-charcoal_ink flex flex-col border-r border-charcoal_ink/10"
            >
              <div className="h-[110px] px-8 flex items-center justify-between border-b border-charcoal_ink/10">
                <Logo variant="light" size={100} />
                <button
                  onClick={() => setMenuOpen(false)}
                  className="w-10 h-10 grid place-items-center text-charcoal_ink hover:bg-glow_lime hover:text-charcoal transition-colors"
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto px-8 py-10">
                <div className="chapter-num text-charcoal_ink/50 mb-6">NAVIGATE</div>
                <ul className="space-y-1">
                  {LINKS.map((l, i) => (
                    <motion.li
                      key={l.label}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 + i * 0.06, duration: 0.5 }}
                      className="border-b border-charcoal_ink/10"
                    >
                      <a
                        href={l.href}
                        onClick={() => setMenuOpen(false)}
                        className="group flex items-center justify-between py-5 hover:pl-2 transition-all duration-300"
                      >
                        <span className="font-serif-display text-4xl md:text-5xl text-charcoal_ink group-hover:text-glow_lime group-hover:italic transition-all">
                          {l.label}
                        </span>
                        <ArrowUpRight className="w-5 h-5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-glow_lime glow-icon" />
                      </a>
                    </motion.li>
                  ))}
                </ul>

                <div className="mt-12">
                  <div className="chapter-num text-charcoal_ink/50 mb-4">STUDIO</div>
                  <p className="text-charcoal_ink/80 text-sm leading-relaxed max-w-sm">
                    MGM Heights, Plot 140/9957,<br />
                    K living · 3rd Floor, Kalinga Nagar,<br />
                    Bhubaneswar 751003, Odisha.
                  </p>
                </div>

                <div className="mt-8 space-y-3">
                  <a href="tel:08908101907" className="flex items-center gap-3 text-charcoal_ink hover:text-glow_lime transition-colors">
                    <Phone className="w-4 h-4 text-glow_lime glow-icon" />
                    <span className="text-base">089081 01907</span>
                  </a>
                  <a href="mailto:studio@kliving.in" className="flex items-center gap-3 text-charcoal_ink hover:text-glow_lime transition-colors">
                    <Mail className="w-4 h-4 text-glow_lime glow-icon" />
                    <span className="text-base">studio@kliving.in</span>
                  </a>
                </div>

                <div className="mt-10 border-t border-charcoal_ink/10 pt-6">
                  <div className="chapter-num text-charcoal_ink/50 mb-3">DEMO VERSIONS</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to="/" className="chapter-num text-charcoal_ink/70 hover:text-glow_lime border border-charcoal_ink/25 rounded-full px-3 py-1.5 tracking-[0.24em]">
                      DEMO 01
                    </Link>
                    <span className="chapter-num text-charcoal bg-glow_lime rounded-full px-3 py-1.5 tracking-[0.24em] glow-box">
                      DEMO 02
                    </span>
                    <Link to="/v3" className="chapter-num text-charcoal_ink/70 hover:text-glow_lime border border-charcoal_ink/25 rounded-full px-3 py-1.5 tracking-[0.24em]">
                      DEMO 03
                    </Link>
                  </div>
                </div>
              </nav>

              <div className="border-t border-charcoal_ink/10 px-8 py-5 flex items-center justify-between chapter-num text-charcoal_ink/50">
                <span>© KLIVING · MMXXV</span>
                <Link to="/admin/login" className="hover:text-glow_lime">ADMIN →</Link>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* SEARCH OVERLAY — dark */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-charcoal"
            onClick={() => setSearchOpen(false)}
          >
            <motion.div
              initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -40, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative h-full flex flex-col v2-gridlines"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-charcoal_ink/10">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[110px] flex items-center justify-between">
                  <div className="chapter-num text-charcoal_ink/60">SEARCH KLIVING</div>
                  <button onClick={() => setSearchOpen(false)} className="w-10 h-10 grid place-items-center text-charcoal_ink hover:bg-glow_lime hover:text-charcoal transition-colors" aria-label="Close search">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 flex flex-col">
                <div className="max-w-[1400px] mx-auto w-full px-6 md:px-10 pt-16 md:pt-24">
                  <div className="flex items-center gap-4 border-b border-charcoal_ink/25 focus-within:border-glow_lime transition-colors">
                    <Search className="w-6 h-6 md:w-7 md:h-7 text-charcoal_ink/60" />
                    <input
                      autoFocus
                      placeholder="Search projects, locations, typologies…"
                      className="flex-1 bg-transparent border-0 py-5 md:py-6 text-charcoal_ink text-2xl md:text-4xl font-serif-display placeholder:text-charcoal_ink/30 focus:outline-none"
                    />
                  </div>

                  <div className="mt-12">
                    <div className="chapter-num text-charcoal_ink/50 mb-4">TOP SEARCHES</div>
                    <div className="flex flex-wrap gap-3">
                      {["Azure Residences", "KLiving Plaza", "Meridian Exchange", "3 BHK Bhubaneswar", "Ready to move", "Ongoing"].map((s) => (
                        <a
                          key={s}
                          href="#projects-v2"
                          onClick={() => setSearchOpen(false)}
                          className="px-4 py-2 border border-charcoal_ink/20 hover:border-glow_lime hover:text-glow_lime text-sm transition-colors duration-300"
                        >
                          {s}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
